/**
 * Developer API: Venue Shows service
 *
 * Backs GET /api/venues/{venueId}/shows on developers.punchup.live. Resolves
 * the venue's upstream ticketing partner (ticketing_partners), fetches its
 * public events, and emits them through the DEFAULT-DENY DeveloperApiShow
 * transform - only contract fields leave this module; ticket types, prices,
 * quantities, and tag ids never do. The upstream is invisible: asset URLs are
 * rewritten to the developers-app asset redirect and sold-out / low-tickets /
 * free are derived server-side as coarse booleans. Lineups come from the
 * Punchup shows the events map to, not from the upstream.
 *
 * The listing hides what punchup.live hides: a password-protected event, an
 * event the upstream marks hidden, and a Punchup show the venue page has set
 * to HIDDEN. An upstream page filter cannot express those rules, so the
 * fetcher gets the partner's whole live-or-scheduled set (in upstream pages,
 * cached once per partner), filters it, sorts it, and only then slices a page.
 * `total` is therefore exact, and the soonest-first order holds across pages.
 */

import { CacheEntities } from "@punchup/utils/caches/cache-types";
import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";
import { fetchWithCache, generateCacheKey } from "@punchup/utils/caches";
import { stripHtml } from "@punchup/utils/stripHtml";
import dayjs from "@punchup/utils/dayjs";

import { createSupabaseAdminClient } from "../../supabase/supabase";
import { getTicketingPartner } from "../../legacy/getTicketingPartner";
import { getShowsByIds } from "../../legacy/shows";
import {
  TixologiResumedEvent,
  getTixologiEventsPage,
} from "../../legacy/tixologi";
import { getTixologiEventImage } from "../../legacy/tixologi-event-image";
import { getPunchupShowIdsByTixologiIds } from "../public/shows";
import {
  DeveloperApiLineupMember,
  DeveloperApiPagination,
  DeveloperApiShow,
  DeveloperApiShowTime,
} from "../../models/developer-api.types";
import { isShowHiddenOnVenuePage } from "../../models/show-visibility";
import {
  HiddenEventIdResolver,
  punchupShowUrl,
  toDeveloperApiLineup,
} from "./shared";
import { rewriteAssetUrl } from "./asset-proxy";

// Upstream events are public data refreshed at most once a minute per partner.
const CACHE_TTL_SECONDS = 60;

// One deadline per request for the whole upstream page loop, retries
// included, so a hung backend fails into the route's designed UPSTREAM_ERROR
// envelope instead of a bare platform 504.
const UPSTREAM_TIMEOUT_MS = 10_000;

// One retry after the first failure. Its one-second backoff stays inside the
// route's 15-second budget; the cache helper's default of three would not.
const UPSTREAM_RETRIES = 1;

// The upstream's largest page size, and a cap on pages per listing.
const UPSTREAM_PAGE_SIZE = 100;
const UPSTREAM_MAX_PAGES = 20;

export interface GetVenueShowsParams {
  page: number;
  perPage: number;
  // Origin of the serving app (e.g. https://developers.punchup.live), used to
  // absolutize rewritten asset URLs.
  assetBaseUrl: string;
  resolveHiddenEventIds: HiddenEventIdResolver;
}

export type GetVenueShowsResult =
  | { ok: true; shows: DeveloperApiShow[]; pagination: DeveloperApiPagination }
  // The venue page exists but has no ticketing partner - callers map this to
  // a designed 404 rather than the upstream's bare one.
  | { ok: false; reason: "not_ticketing" };

/** The instant when the zone is known, else the wall time; both sort as ISO strings. */
function startKey(show: DeveloperApiShow): string {
  return show.start.utc ?? show.start.local;
}

/** Soonest first. A show with no parseable start has an empty key and goes last, never first. */
function compareByStart(a: DeveloperApiShow, b: DeveloperApiShow): number {
  const keyA = startKey(a);
  const keyB = startKey(b);
  if (!keyA || !keyB) {
    return Number(!keyA) - Number(!keyB);
  }
  return keyA.localeCompare(keyB);
}

/** All shows the developer API serves for one venue page. */
export async function getVenueShowsForDeveloperApi(
  venuePageId: string,
  params: GetVenueShowsParams
): Promise<GetVenueShowsResult> {
  const supabase = createSupabaseAdminClient();

  const partner = await getTicketingPartner(supabase, venuePageId);
  if (!partner?.tixologi_partner_id) {
    return { ok: false, reason: "not_ticketing" };
  }

  // Redis-backed cache per the caching spec (fetchWithCache fails open to a
  // direct fetch); keyed per partner so every token and every page share one
  // warm entry. The `v3` suffix names the cached shape: the partner's whole
  // event array.
  const signal = AbortSignal.timeout(UPSTREAM_TIMEOUT_MS);
  const events = await fetchWithCache(
    generateCacheKey(
      CacheEntities.VENUE_TICKETING_EVENTS,
      `${partner.tixologi_partner_id}:all:v3`
    ),
    () => fetchAllPartnerEvents(Number(partner.tixologi_partner_id), signal),
    CACHE_TTL_SECONDS,
    { retries: UPSTREAM_RETRIES }
  );

  const hiddenEventIds = await params.resolveHiddenEventIds(
    events.map((event) => String(event.id))
  );
  const visibleEvents = events.filter(
    (event) =>
      event.is_password_protected !== true &&
      !hiddenEventIds.has(String(event.id))
  );

  // Map upstream event ids -> Punchup show pages so ticketUrl can point at
  // punchup.live. Events without a mapped show emit ticketUrl: null.
  const showIdByEventId = await getPunchupShowIdsByTixologiIds(
    visibleEvents.map((e) => String(e.id))
  );

  // The same mapping carries the lineup: the Punchup show rows behind those
  // ids hold the comedian pairings. An unmapped event emits an empty lineup,
  // the same population that already emits ticketUrl: null.
  const showsById = await getShowsByIds(Object.values(showIdByEventId));

  const shows = visibleEvents.flatMap((event) => {
    const showId = showIdByEventId[String(event.id)];
    const show = showId ? showsById.get(showId) : undefined;
    if (show && isShowHiddenOnVenuePage(show, venuePageId)) {
      return [];
    }
    return toDeveloperApiShow(event, {
      assetBaseUrl: params.assetBaseUrl,
      ticketUrl: showId ? punchupShowUrl(showId) : null,
      lineup: toDeveloperApiLineup(show?.show_comedians),
      posterUrl: show?.poster_img ? getCDNImageURL(show.poster_img) : null,
    });
  });

  // Soonest first. The upstream does not promise an order, and the contract
  // documents one, so sort the whole filtered set before slicing a page.
  shows.sort(compareByStart);
  const start = (params.page - 1) * params.perPage;

  return {
    ok: true,
    shows: shows.slice(start, start + params.perPage),
    pagination: {
      page: params.page,
      perPage: params.perPage,
      // Never null on this path; the contract type stays nullable.
      total: shows.length,
      hasMore: start + params.perPage < shows.length,
    },
  };
}

/**
 * The partner's whole live-or-scheduled set, in upstream pages until the
 * first page's total is reached or a short page returns, capped at
 * UPSTREAM_MAX_PAGES. One `signal` bounds every page. An empty page below
 * the total is a truncated set: the throw keeps it out of the cache.
 */
async function fetchAllPartnerEvents(
  partnerId: number,
  signal: AbortSignal
): Promise<TixologiResumedEvent[]> {
  const events: TixologiResumedEvent[] = [];
  let total: number | null = null;
  for (let page = 1; page <= UPSTREAM_MAX_PAGES; page++) {
    const batch = await getTixologiEventsPage({
      partner_id: partnerId,
      date: "live_or_scheduled",
      page,
      perPage: UPSTREAM_PAGE_SIZE,
      signal,
    });
    if (page === 1) {
      total = batch.total;
    }
    events.push(...batch.events);
    if (total !== null && events.length >= total) {
      break;
    }
    if (total !== null && batch.events.length === 0) {
      throw new Error(
        "Tixologi returned an empty page before the reported total"
      );
    }
    if (batch.events.length < UPSTREAM_PAGE_SIZE) {
      break;
    }
  }
  return events;
}

// =============================================================================
// Transform (exported for tests)
// =============================================================================

/**
 * The default-deny transform: an upstream event in, ONLY contract fields out.
 * Never spread the event - every emitted field is named here.
 */
export function toDeveloperApiShow(
  event: TixologiResumedEvent,
  opts: {
    assetBaseUrl: string;
    ticketUrl: string | null;
    lineup: DeveloperApiLineupMember[];
    posterUrl: string | null;
  }
): DeveloperApiShow {
  return {
    id: String(event.id),
    checkoutEventId: String(event.id),
    title: event.name,
    description: deriveDescription(event),
    venue: event.venue || null,
    location: event.street_address || null,
    start: toShowTime(event.start_date, event.time_zone),
    end: event.end_date ? toShowTime(event.end_date, event.time_zone) : null,
    isSoldOut: deriveIsSoldOut(event),
    hasLowTickets: deriveHasLowTickets(event),
    isFree: deriveIsFree(event),
    tags: deriveTags(event),
    lineup: opts.lineup,
    imageUrl:
      rewriteAssetUrl(getTixologiEventImage(event), opts.assetBaseUrl) ??
      opts.posterUrl,
    ticketUrl: opts.ticketUrl,
  };
}

/**
 * Render an upstream instant as the contract's wall-clock time in the event's
 * own zone, with the instant itself alongside as `utc`. The upstream stores an
 * instant plus `time_zone` (unlike `comedian_shows.datetime`, which is wall
 * time already), so this is an exact rendering, never a guess. A missing or
 * unknown zone names no clock, so the whole value degrades to `local: ""`
 * with null `timezone` and `utc`; an unparseable instant degrades to `local:
 * ""` and null `utc` the way the other fields do, rather than 500ing the
 * endpoint.
 */
export function toShowTime(
  instant: string | null | undefined,
  timeZone: string | null | undefined
): DeveloperApiShowTime {
  const unknownZone = { local: "", timezone: null, utc: null };
  if (!timeZone) {
    return unknownZone;
  }
  const moment = instant ? dayjs(instant) : null;
  if (!moment?.isValid()) {
    return { local: "", timezone: timeZone, utc: null };
  }
  try {
    return {
      local: moment.tz(timeZone).format("YYYY-MM-DDTHH:mm:ss"),
      timezone: timeZone,
      utc: moment.utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
    };
  } catch {
    // dayjs throws on a zone name Intl does not know.
    return unknownZone;
  }
}

/**
 * Upstream descriptions are authored as rich text. Flatten to plain text so a
 * consumer can render the value anywhere without inheriting someone else's
 * markup - the same treatment the description gets on punchup.live.
 */
export function deriveDescription(event: TixologiResumedEvent): string | null {
  if (!event.description) {
    return null;
  }
  return stripHtml(event.description) || null;
}

/**
 * Free when any ticket a buyer can actually see is priced at zero or is
 * pay-what-you-want - the same rule the public Free Shows page uses. Coarse by
 * design: the boolean ships, the prices behind it never do. Null when the
 * event carries no visible ticket types, so "unknown" stays distinct from
 * "not free".
 */
export function deriveIsFree(event: TixologiResumedEvent): boolean | null {
  const visible = event.ticket_types?.filter((t) => t.ticket_visibility);
  if (!visible?.length) {
    return null;
  }
  return visible.some(
    (t) => t.pay_what_you_want === true || t.initial_price === 0
  );
}

/**
 * Category labels as NAMES only. The upstream tag ids are backend keys and
 * never leave this module; a consumer filters on the human-readable name.
 * Deduped and trimmed, order preserved.
 */
export function deriveTags(event: TixologiResumedEvent): string[] {
  const names = (event.tags ?? [])
    .map((tag) => tag.name?.trim())
    .filter((name): name is string => !!name);
  return [...new Set(names)];
}

/**
 * Sold out when every ticket type reports sold_out; falls back to the event's
 * aggregate counts when ticket types are absent from the public payload.
 */
export function deriveIsSoldOut(event: TixologiResumedEvent): boolean {
  if (event.ticket_types && event.ticket_types.length > 0) {
    return event.ticket_types.every((tt) => tt.sold_out);
  }
  return event.quantity > 0 && event.tickets_sold >= event.quantity;
}

/**
 * Coarse urgency boolean: remaining capacity at or under 10% (min threshold
 * 5), and at least one ticket sold - so an untouched small room never opens
 * in the "low" state. Deliberately never exposes the underlying numbers.
 */
export function deriveHasLowTickets(event: TixologiResumedEvent): boolean {
  if (deriveIsSoldOut(event)) {
    return false;
  }
  if (!event.quantity || event.quantity <= 0 || event.tickets_sold <= 0) {
    return false;
  }
  const remaining = event.quantity - event.tickets_sold;
  return (
    remaining > 0 && remaining <= Math.max(5, Math.ceil(event.quantity * 0.1))
  );
}
